/**
 * Importing from more than the Google Sheet:
 *
 *   - A Split Ledger download (the JSON from Your settings → Download your data): pick a group
 *     and its people, stores, items, payments, receipt totals and catalog notes are added.
 *   - A CSV with a header row: our own "Items as a spreadsheet" file, or any sheet whose
 *     columns are named like Date, Store, Description, Price, Split between.
 *
 * Every change gets a stable id, so importing the same file twice adds nothing the second time.
 */
import { fold } from "../apply.ts";
import type { CommandEnvelope } from "../commands.ts";
import type { StoredEvent } from "../events.ts";
import { parseMoney, parsePercentBp } from "../money.ts";
import { PALETTE, RECEIPT_ID_PREFIX, type GroupState } from "../types.ts";
import { sheetDate, type ImportPlan } from "./sheet.ts";

function slug(name: string): string {
  return (
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "x"
  );
}

// ------------------------------------------------------------------ our own download

export interface ExportFile {
  kind: "split-ledger-export";
  format: number;
  groups: { id: string; name: string; events: StoredEvent[] }[];
}

export function isExportFile(v: unknown): v is ExportFile {
  return (
    !!v &&
    typeof v === "object" &&
    (v as { kind?: unknown }).kind === "split-ledger-export" &&
    Array.isArray((v as { groups?: unknown }).groups)
  );
}

/** Each group in a download, rebuilt from its history. */
export function exportGroups(file: ExportFile): { id: string; name: string; state: GroupState }[] {
  return file.groups.map((g) => ({ id: g.id, name: g.name, state: fold(g.id, g.events) }));
}

/** The commands that recreate a group's current contents (not its history) in another group. */
export function planStateImport(state: GroupState, source: string): ImportPlan {
  const tag = `import:${slug(source)}`;
  const commands: CommandEnvelope[] = [];
  const push = (id: string, cmd: CommandEnvelope["cmd"]) =>
    commands.push({ cmdId: `${tag}:${id}`, cmd });
  push("mark", { type: "import.mark", source, capturedAt: new Date().toISOString().slice(0, 10) });

  for (const st of Object.values(state.stores)) {
    push(`store:${slug(st.name)}`, {
      type: "store.set",
      name: st.name,
      taxBp: st.taxBp,
      category: st.category,
      ...(st.tipBp ? { tipBp: st.tipBp } : {}),
      ...(st.mcc ? { mcc: st.mcc } : {}),
    });
  }
  const personIds: Record<string, string> = {};
  for (const id of state.personOrder) {
    const p = state.people[id];
    if (!p) continue;
    personIds[p.name.toLowerCase()] = p.id;
    push(`person:${p.id}`, { type: "person.add", personId: p.id, name: p.name, color: p.color });
    if (p.archived)
      push(`archive:${p.id}`, { type: "person.update", personId: p.id, patch: { archived: true } });
  }
  push("settings", {
    type: "settings.update",
    patch: {
      categories: state.settings.categories,
      cards: state.settings.cards,
      overdueWarnDays: state.settings.overdueWarnDays,
      overdueLateDays: state.settings.overdueLateDays,
      ...(state.settings.ownerPersonId && state.people[state.settings.ownerPersonId]
        ? { ownerPersonId: state.settings.ownerPersonId }
        : {}),
    },
  });
  for (const id of state.itemOrder) {
    const it = state.items[id];
    if (!it) continue;
    const { id: itemId, ticked, ...fields } = it;
    push(`item:${itemId}`, {
      type: "item.add",
      itemId,
      fields,
      ticked: ticked.filter((p) => state.people[p]),
    });
  }
  for (const id of state.paymentOrder) {
    const p = state.payments[id];
    if (!p || !state.people[p.personId]) continue;
    push(`payment:${p.id}`, {
      type: "payment.add",
      paymentId: p.id,
      personId: p.personId,
      cents: p.cents,
      at: p.at,
      note: p.note,
    });
  }
  for (const [key, cents] of Object.entries(state.receipts)) {
    // A receipt kept by id (2026-10-28): its items say its store, date and label today.
    if (key.startsWith(RECEIPT_ID_PREFIX)) {
      const receiptId = key.slice(RECEIPT_ID_PREFIX.length);
      const it = Object.values(state.items).find((i) => i.receiptId === receiptId);
      if (!it) continue;
      push(`receipt:${slug(key)}`, {
        type: "receipt.set",
        date: it.date,
        store: it.store,
        ...(it.receipt ? { receipt: it.receipt } : {}),
        receiptId,
        cents,
      });
      continue;
    }
    const [date = "", store = "", receipt] = key.split("|");
    push(`receipt:${slug(key)}`, {
      type: "receipt.set",
      date,
      store,
      ...(receipt ? { receipt } : {}),
      cents,
    });
  }
  for (const [key, note] of Object.entries(state.catalogNotes ?? {})) {
    push(`note:${slug(key)}`, { type: "catalog.note", key, note });
  }
  return { commands, problems: [], personIds };
}

// ------------------------------------------------------------------ CSV

/** Splits CSV text into rows, handling quotes, doubled quotes and newlines inside quotes. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const src = text.replace(/^\uFEFF/, "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

const ALIASES: Record<string, string[]> = {
  date: ["date", "day", "purchased"],
  store: ["store", "merchant", "where", "shop"],
  receipt: ["receipt"],
  itemNo: ["item #", "item no", "item number", "item#", "sku"],
  name: ["description", "item", "name", "product"],
  category: ["category"],
  price: ["price", "amount", "cost"],
  coupon: ["coupon", "discount"],
  crv: ["crv", "deposit"],
  tax: ["tax %", "tax", "tax/tip %", "tax rate"],
  tip: ["tip %", "tip"],
  card: ["card", "paid with", "payment method"],
  paidBy: ["paid by"],
  people: ["split between", "people", "who", "shared by", "for"],
  note: ["note", "notes"],
};

/** Which known column each header is, for showing before importing. */
export function csvColumns(header: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  header.forEach((h, i) => {
    const k = h.trim().toLowerCase();
    for (const [field, names] of Object.entries(ALIASES))
      if (out[field] === undefined && names.includes(k)) out[field] = i;
  });
  return out;
}

/** "10/12/2026", "2026-10-12" or "Oct 12, 2026" -> "2026-10-12". */
function readDate(text: string): string | null {
  const t = text.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const s = sheetDate(t);
  if (s) return s;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

/** Items from a CSV, adding anyone in "Split between" who isn't in the group yet. */
export function planCsvImport(text: string, existing: GroupState, source: string): ImportPlan {
  const tag = `import:${slug(source)}`;
  const rows = parseCsv(text);
  const commands: CommandEnvelope[] = [];
  const problems: string[] = [];
  const push = (id: string, cmd: CommandEnvelope["cmd"]) =>
    commands.push({ cmdId: `${tag}:${id}`, cmd });
  if (rows.length < 2)
    return { commands, problems: ["The file has no rows under its header."], personIds: {} };
  const C = csvColumns(rows[0]!);
  for (const need of ["date", "name", "price"]) {
    if (C[need] === undefined)
      problems.push(
        `No "${need === "name" ? "Description" : need[0]!.toUpperCase() + need.slice(1)}" column found.`,
      );
  }
  if (problems.length) return { commands, problems, personIds: {} };

  const personIds: Record<string, string> = {};
  for (const p of Object.values(existing.people)) personIds[p.name.toLowerCase()] = p.id;
  let colorAt = Object.keys(existing.people).length;
  const idFor = (name: string): string => {
    const key = name.trim().toLowerCase();
    if (personIds[key]) return personIds[key]!;
    const id = `p-${slug(name)}`;
    personIds[key] = id;
    push(`person:${id}`, {
      type: "person.add",
      personId: id,
      name: name.trim(),
      color: PALETTE[colorAt++ % PALETTE.length]!,
    });
    return id;
  };

  push("mark", { type: "import.mark", source, capturedAt: new Date().toISOString().slice(0, 10) });
  rows.slice(1).forEach((row, i) => {
    const cell = (f: string) => (C[f] === undefined ? "" : (row[C[f]!] ?? "").trim());
    const line = i + 2;
    const date = readDate(cell("date"));
    const price = parseMoney(cell("price"));
    const name = cell("name");
    if (!date || price === null || !name) {
      problems.push(`Row ${line}: needs a date, a description and a price`);
      return;
    }
    const coupon = cell("coupon") ? parseMoney(cell("coupon")) : 0;
    const crv = cell("crv") ? parseMoney(cell("crv")) : 0;
    const tax = cell("tax") ? parsePercentBp(cell("tax")) : null;
    const tip = cell("tip") ? parsePercentBp(cell("tip")) : null;
    if (
      coupon === null ||
      crv === null ||
      (cell("tax") && tax === null) ||
      (cell("tip") && tip === null)
    ) {
      problems.push(`Row ${line}: couldn't read a coupon, CRV, tax or tip amount`);
      return;
    }
    const people = cell("people")
      .split(/\s*[;,]\s*/)
      .map((s) => s.replace(/\s+\$?[\d.]+$/, "").trim())
      .filter(Boolean);
    const store = cell("store") || "Store";
    const receipt = cell("receipt");
    push(`item:${line}`, {
      type: "item.add",
      itemId: `${tag}-${line}`,
      fields: {
        date,
        store,
        itemNo: cell("itemNo").replace(/^#/, ""),
        name,
        category: cell("category") || null,
        priceCents: price,
        couponCents: -Math.abs(coupon),
        crvCents: Math.abs(crv),
        taxBp: tax,
        ...(tip !== null ? { tipBp: tip } : {}),
        paidWith: cell("card") || null,
        paidBy: cell("paidBy") ? idFor(cell("paidBy")) : null,
        customSplit: "",
        note: cell("note"),
        ...(receipt ? { receipt } : {}),
      },
      ticked: [...new Set(people.map(idFor))],
    });
  });
  return { commands, problems, personIds };
}
